import React, { useState, useEffect } from "react";
import { Layout } from "../components";
import { Link } from "react-router-dom";
import { fetchAllIndustries } from "../api/strapi/industries";
import { useIntl } from "react-intl";
import DataLoader from "../components/loader/DataLoader";
import PageHero from "../components/common/PageHero";
import SectionHeader from "../components/common/SectionHeader";
import SkeletonImage from "../components/common/SkeletonImage";

const Industries = () => {
  const intl = useIntl();
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setLoading(true);
        const result = await fetchAllIndustries();

        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Industries data unavailable:", result);
            setError(intl.formatMessage({ id: "common.contentUnavailable" }));
          }
          setIndustries([]);
          return;
        }

        setIndustries(Array.isArray(result.data) ? result.data : []);
      } catch (err) {
        console.error("Erreur lors du chargement des industries:", err);
        setError(intl.formatMessage({ id: "common.contentUnavailable" }));
        setIndustries([]);
      } finally {
        setLoading(false);
      }
    };

    loadIndustries();
  }, [intl]);

  if (loading) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.loading" }) }}>
        <DataLoader isLoading={true} />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.unavailable" }) }}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-slate-500 text-center">
            <h2 className="text-2xl font-bold mb-2 text-[#0a1628]">{intl.formatMessage({ id: "common.contentUnavailableTitle" })}</h2>
            <p>{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout data={{ title: intl.formatMessage({ id: "industries.title", defaultMessage: "Industries" }) }}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <PageHero
          eyebrow="Africa Systems"
          title={intl.formatMessage({ id: "industries.title", defaultMessage: "Our Industries" })}
          description={intl.formatMessage({ id: "industries.description", defaultMessage: "Explore our solutions across various industry sectors." })}
        />

        {/* Industries Grid */}
        <div className="py-16">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <SectionHeader
              eyebrow="Africa Systems"
              title={intl.formatMessage({ id: "industries.title", defaultMessage: "Our Industries" })}
              description={intl.formatMessage({ id: "industries.description", defaultMessage: "Explore our solutions across various industry sectors." })}
            />
            {industries.length === 0 ? (
              <div className="text-center py-12">
                <h2 className="text-2xl font-semibold text-[#0a1628] mb-4">
                  {intl.formatMessage({ id: "industries.noneAvailable", defaultMessage: "No industries available" })}
                </h2>
                <p className="text-slate-500">
                  {intl.formatMessage({ id: "industries.checkBackLater", defaultMessage: "Please check back later for new industries." })}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {industries.map((industry) => {
                  const industryData = industry.attributes || industry;
                  return (
                    <Link
                      key={industry.id || industryData.id}
                      to={`/info/industries/${industryData.slug}`}
                      className="block group"
                    >
                      <div className="group bg-white border border-slate-200 hover:border-[#16A34A]/40 hover:shadow-lg transition-all duration-300 h-full flex flex-col overflow-hidden">
                        {industryData.heroComponent?.image && (
                          <SkeletonImage
                            className="h-48"
                            src={`${process.env.REACT_APP_STRAPI_URL || 'http://156.67.104.166:1338'}${industryData.heroComponent.image.url}`}
                            alt={industryData.heroComponent.title}
                            imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            overlay={<div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />}
                          />
                        )}

                        <div className="p-6 flex flex-col flex-grow">
                          <h3 className="text-xl font-bold text-[#0a1628] mb-2 group-hover:text-[#16A34A] transition-colors">
                            {industryData.heroComponent?.title || industryData.title}
                          </h3>

                          <p className="text-slate-600 mb-4 flex-grow">
                            {industryData.heroComponent?.description || industryData.description}
                          </p>

                          <div className="mt-auto">
                            <span className="inline-flex items-center gap-2 bg-[#16A34A] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#15803D] transition-colors w-fit">
                              {intl.formatMessage({ id: "industries.learnMore", defaultMessage: "Learn More" })}
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Industries;
